import * as React from 'react'
import { Link } from 'gatsby'
import Helmet from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLinkedin } from '@fortawesome/free-brands-svg-icons'
import { faBuilding, faEnvelopeSquare, faPhoneSquare } from '@fortawesome/free-solid-svg-icons'
import bridgephaseLight from '../../static/images/logos/bridgephase-light.svg'
import {
  AccessibilityToggles,
  TOGGLE_KEY_HIGH_CONTRAST,
  TOGGLE_KEY_LARGE_TEXT,
} from '../components/accessibility-toggles'
import useLocalStorage from 'react-use-localstorage'

const PAGES = [
  { title: 'Home', href: '/' },
  { title: 'What We Do', href: '/what-we-do' },
  { title: 'Insights', href: '/insights' },
  { title: 'About', href: '/about' },
  { title: 'Contract Vehicles', href: '/contract-vehicles' },
  { title: 'Careers', href: '/careers' },
  { title: 'BridgePhase Gear', href: 'https://bridgephase.axomo.com', target: '_blank', rel: 'noreferrer' },
]

interface SiteLinkProps {
  page: {
    title: string
    href: string
    target?: string
    rel?: string
  }
  className: string
}
const SiteLink: React.FC<SiteLinkProps> = ({ page, className }) => {
  if (page.href.startsWith('http')) {
    return (
      <a href={page.href} target={page.target} rel={page.rel} className={className}>
        {page.title}
      </a>
    )
  } else {
    return (
      <Link to={page.href} target={page.target} rel={page.rel} className={className} key={page.title}>
        {page.title}
      </Link>
    )
  }
}

const Header = ({ name }: { name: string }) => {
  const [scrollPercent, setScrollPercent] = React.useState(0)
  const [mobileMenuExpanded, setMobileMenuExpanded] = React.useState(false)
  React.useEffect(() => {
    document.addEventListener('scroll', (ev: Event) => {
      const scrollTop = window.scrollY
      const docHeight = document.body.offsetHeight
      const winHeight = window.innerHeight
      const scrollPercentNormalized = scrollTop / (docHeight - winHeight)
      setScrollPercent(Math.round(scrollPercentNormalized * 100))
    })
  })
  return (
    <nav className="main-nav pt-2">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-10">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/">
                <img className="h-24 w-32" src={bridgephaseLight} alt="BridgePhase logo" title="BridgePhase, LLC" />
              </Link>
            </div>
            <div className="hidden xl:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {PAGES.map((page) => (
                  <SiteLink
                    page={page}
                    key={page.title}
                    className={`${
                      page.title === name
                        ? 'bg-gray-900 text-white hover:text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } px-3 py-2 rounded-md text-sm font-medium suppress-visited`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="xl:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuExpanded(!mobileMenuExpanded)}
              className={`${
                mobileMenuExpanded ? 'bg-gray-800 text-gray-400' : 'text-gray-200'
              } inline-flex items-center justify-center p-2 rounded-md hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white`}
              aria-controls="mobile-menu"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className="hidden h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuExpanded && (
        <div className="xl:hidden" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {PAGES.map((page) => (
              <SiteLink
                key={page.title}
                page={page}
                className={`${
                  page.title === name ? 'active' : 'text-gray-300'
                } hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md font-medium`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="scroll-progress" style={{ width: `${scrollPercent}%` }} />
    </nav>
  )
}

const Footer = () => (
  <footer className="dark">
    <div className="flex w-full gap-3 flex-col md:flex-row">
      <div className="flex flex-col gap-3">
        <a
          href="https://www.linkedin.com/company/bridgephase"
          className="contact-link suppress-visited justify-center lg:justify-start"
          target="_blank"
        >
          <FontAwesomeIcon icon={faLinkedin} />
          BridgePhase LinkedIn
        </a>
        <a href="mailto:info@bridgephase.com" className="contact-link suppress-visited justify-center lg:justify-start">
          <FontAwesomeIcon icon={faEnvelopeSquare} />
          info@bridgephase.com
        </a>
        <a href="tel:1-571-347-1510" className="contact-link suppress-visited justify-center lg:justify-start">
          <FontAwesomeIcon icon={faPhoneSquare} />
          (571) 347-1510
        </a>
        <a href="https://www.google.com/maps/dir/?api=1&destination=5935%20King%20Street%20Mt%20Jackson%2C%20VA%2022842" target="_blank" rel="noreferrer" className="contact-link suppress-visited justify-center lg:justify-start">
          <FontAwesomeIcon icon={faBuilding} />
          <div className="text-sm">5935 King St.<br />Mt Jackson, VA 22842</div>
        </a>
      </div>
      <div className="justify-center grow">
        {/* <div className="mb-3 text-center">
          <div className="text-sm opacity-75 mb-1">Mailing Address:</div>
          <div className="text-sm">5935 King St.</div>
          <div className="text-sm">Mt Jackson, VA 22842</div>
        </div>
 */}
        {/* <div className="text-center grid grid-cols-1 xl:grid-cols-3"> */}
          
          {/* <div className="mb-3">
            <div className="text-sm opacity-75 mb-1">Mailing Address:</div>
            <div className="text-sm">5935 King St.</div>
            <div className="text-sm">Mt Jackson, VA 22842</div>
          </div> */}
        {/* </div> */}
      </div>
      <ul>
        {PAGES.map((page) => (
          <li key={page.title} className="mb-1">
            <SiteLink page={page} key={page.title} className="" />
          </li>
        ))}
      </ul>
    </div>
    <div className="text-xs text-center mt-3">Copyright &copy;2025 BridgePhase, LLC.</div>
  </footer>
)

const generateBdhrListings = () => {
  if (typeof bdhr === 'undefined') {
    setTimeout(generateBdhrListings, 500)
  } else {
    const options = {
      CCPCode: 'BridgePhaseCareerPage',
      ElementID: 'BDHRJobListings',
      ShowCustomContent: false,
    }
    bdhr.generateListing(options)
  }
}

export function DefaultLayout({
  name,
  description,
  children,
}: {
  name: string
  description: string
  children: React.ReactNode
}) {
  let highContrast = 'false'
  let largeText = 'false'
  let setHighContrast = (newValue: string) => {}
  let setLargeText = (newValue: string) => {}
  if (typeof window !== `undefined`) {
    ;[highContrast, setHighContrast] = useLocalStorage(TOGGLE_KEY_HIGH_CONTRAST)
    ;[largeText, setLargeText] = useLocalStorage(TOGGLE_KEY_LARGE_TEXT)
  }
  return (
    <div
      id="main-layout"
      className={`contrast-${highContrast === 'true' ? 'high' : 'normal'} text-size-${
        largeText === 'true' ? 'large' : 'normal'
      }`}
    >
      <Helmet
        title={`BridgePhase » ${name}`}
        onChangeClientState={(newState, addedTags, removedTags) => {
          if (newState.scriptTags.length > 0) {
            generateBdhrListings()
          }
        }}
        meta={[
          {
            name: 'description',
            content: description || 'BridgePhase, LLC Homepage',
          },
          { name: 'theme-color', content: '#AF3036' },
          {
            name: 'keywords',
            content: 'bridgephase, software, software-development',
          },
        ]}
      >
        <link rel="stylesheet" href="//jobs.ourcareerpages.com/Resources/css/ccp_widget_support.css" />
        <script type="text/javascript" src="//jobs.ourcareerpages.com/Resources/js/ccp_widget_support.js" />
      </Helmet>
      <Header name={name} />
      <main>{children}</main>
      <AccessibilityToggles
        onToggleHighContrast={(newValue: string) => setHighContrast(newValue)}
        onToggleLargeText={(newValue: string) => setLargeText(newValue)}
      />
      <Footer />
    </div>
  )
}
